import {
  AutoEmbedOption,
  LexicalAutoEmbedPlugin,
  URL_MATCHER
} from '@lexical/react/LexicalAutoEmbedPlugin'
import type {
  EmbedConfig,
} from '@lexical/react/LexicalAutoEmbedPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useMemo, useState } from 'react'
import * as React from 'react'
import * as ReactDOM from 'react-dom'

import type { LexicalEditor } from 'lexical'
import useModal from '../hooks/use-modal'
import { DialogActions } from '../ui/dialog'
import { INSERT_FIGMA_COMMAND } from './figma-plugin'
import { INSERT_YOUTUBE_COMMAND } from './you-tube-plugin'
import { Youtube, Codepen } from 'lucide-react'
import { isValidIframe } from '../nodes/figma-node'
import { Button } from '@/components/ui/button'

// Define our own EmbedResult type since it might be missing from the exports or renamed
interface EmbedResult {
  id: string
  url: string
}

export interface PlaygroundEmbedConfig extends EmbedConfig {
  contentName: string;
  exampleUrl: string;
  icon: React.JSX.Element;
  keywords: string[];
}

export const YoutubeEmbedConfig: PlaygroundEmbedConfig = {
  contentName: 'Youtube Video',
  exampleUrl: 'https://www.youtube.com/watch?v=jNQXAC9IVRw',
  icon: <Youtube className='h-5 w-5 text-red-600' />,
  insertNode: (editor: LexicalEditor, result: EmbedResult) => {
    editor.dispatchCommand(INSERT_YOUTUBE_COMMAND, result.id)
  },
  keywords: ['youtube', 'video'],
  parseUrl: async (url: string) => {
    const match = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/.exec(url)
    const id = match ? (match?.[2].length === 11 ? match[2] : null) : null
    if (id != null) {
      return { id, url }
    }
    return null
  },
  type: 'youtube-video'
}

export const FigmaEmbedConfig: PlaygroundEmbedConfig = {
  contentName: 'Embed',
  exampleUrl:
    '<iframe src="https://phet.colorado.edu/sims/html/geometric-optics-basics/latest/geometric-optics-basics_fi.html" width="800" height="600" allowfullscreen></iframe>',
  icon: <Codepen className='h-5 w-5 text-gray-700' />,
  insertNode: (editor: LexicalEditor, result: EmbedResult) => {
    editor.dispatchCommand(INSERT_FIGMA_COMMAND, result.id)
  },
  keywords: ['figma', 'figma.com', 'mock-up'],
  parseUrl: (text: string) => {
    const match =
      /https:\/\/([\w.-]+\.)?figma.com\/(file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/.exec(text)
    if (match != null) {
      return { id: match[3], url: match[0] }
    }
    return null
  },
  type: 'figma'
}

export const EmbedConfigs = [YoutubeEmbedConfig, FigmaEmbedConfig]

function AutoEmbedMenuItem({ index, isSelected, onClick, onMouseEnter, option }: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: AutoEmbedOption;
}) {
  let className = 'item flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100 transition-colors'
  if (isSelected) {
    className += ' bg-gray-100 font-medium'
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role='option'
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <span className='text-sm text-gray-700'>{option.title}</span>
    </li>
  )
}

function AutoEmbedMenu({ options, selectedItemIndex, onOptionClick, onOptionMouseEnter }: {
  options: AutoEmbedOption[];
  selectedItemIndex: number | null;
  onOptionClick: (option: AutoEmbedOption, index: number) => void;
  onOptionMouseEnter: (index: number) => void;
}) {
  return (
    <div className='bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden min-w-[200px] py-1'>
      <ul className='m-0 p-0 list-none'>
        {options.map((option, i) => (
          <AutoEmbedMenuItem
            index={i}
            isSelected={selectedItemIndex === i}
            onClick={() => onOptionClick(option, i)}
            onMouseEnter={() => onOptionMouseEnter(i)}
            key={option.key}
            option={option}
          />
        ))}
      </ul>
    </div>
  )
}

const debounce = (callback: (text: string) => void, delay: number) => {
  let timeoutId: number
  return (text: string) => {
    window.clearTimeout(timeoutId)
    timeoutId = window.setTimeout(() => {
      callback(text)
    }, delay)
  }
}

export function AutoEmbedDialog({ embedConfig, onClose }: { embedConfig: PlaygroundEmbedConfig; onClose: () => void }): React.JSX.Element {
  const [text, setText] = useState('')
  const [textArea, setTextArea] = useState('')
  const [editor] = useLexicalComposerContext()
  const [embedResult, setEmbedResult] = useState<EmbedResult | null>(null)
  const [isValidIFrame, setIsValidIFrame] = useState(false)

  const validateText = useMemo(
    () =>
      debounce((inputText: string) => {
        const urlMatch = URL_MATCHER.exec(inputText)
        if (embedConfig != null && inputText != null && urlMatch != null) {
          Promise.resolve(embedConfig.parseUrl(inputText)).then((parseResult) => {
            setEmbedResult(parseResult)
          })
        } else if (embedResult != null) {
          setEmbedResult(null)
        }
      }, 200),
    [embedConfig, embedResult]
  )

  const validateTextArea = useMemo(
    () =>
      debounce((inputText: string) => {
        const sanitizedIframe = isValidIframe(inputText)
        if (!sanitizedIframe) {
          return setIsValidIFrame(false)
        }
        setIsValidIFrame(true)
        return setEmbedResult({ id: sanitizedIframe, url: inputText })
      }, 200),
    [embedConfig, embedResult]
  )

  const onClick = () => {
    if (embedResult != null) {
      embedConfig.insertNode(editor, embedResult)
      onClose()
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='Input__wrapper'>
        {embedConfig?.type === 'youtube-video' && (
          <input
            type='text'
            className='focus:border-blue-500 border-gray-400 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1.5 outline-none'
            placeholder={embedConfig.exampleUrl}
            value={text}
            data-test-id={`${embedConfig.type}-embed-modal-url`}
            onChange={(e) => {
              const { value } = e.target
              setText(value)
              validateText(value)
            }}
          />
        )}
        {embedConfig?.type === 'figma' && (
          <textarea
            className='focus:border-blue-500 border-gray-400 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1.5 outline-none'
            placeholder={embedConfig.exampleUrl}
            value={textArea}
            rows={5}
            data-test-id={`${embedConfig.type}-embed-modal-url`}
            onChange={(e) => {
              const { value } = e.target
              setTextArea(value)
              validateTextArea(value)
            }}
          ></textarea>
        )}
      </div>
      {embedConfig?.type === 'figma' && !isValidIFrame && textArea && (
        <p className='text-xs font-medium text-red-500'>Please enter valid iframe!</p>
      )}
      <DialogActions>
        <Button
          disabled={!embedResult || (!isValidIFrame && embedConfig?.type === 'figma')}
          onClick={onClick}
          size="sm"
          className="ml-auto"
        >
          Embed
        </Button>
      </DialogActions>
    </div>
  )
}

export default function AutoEmbedPlugin(): React.JSX.Element {
  const [modal, showModal] = useModal()

  const openEmbedModal = (embedConfig: PlaygroundEmbedConfig) => {
    showModal(
      `${embedConfig?.type === 'youtube-video' ? 'Embed ' : ''}${embedConfig.contentName}`,
      (onClose) => <AutoEmbedDialog embedConfig={embedConfig} onClose={onClose} />
    )
  }

  const getMenuOptions = (activeEmbedConfig: PlaygroundEmbedConfig, embedFn: () => void, dismissFn: () => void) => {
    return [
      new AutoEmbedOption('Dismiss', {
        onSelect: dismissFn
      }),
      new AutoEmbedOption(`Embed ${activeEmbedConfig.contentName}`, {
        onSelect: embedFn
      })
    ]
  }

  return (
    <>
      {modal}
      <LexicalAutoEmbedPlugin<PlaygroundEmbedConfig>
        embedConfigs={EmbedConfigs}
        onOpenEmbedModalForConfig={openEmbedModal}
        getMenuOptions={getMenuOptions}
        menuRenderFn={(
          anchorElementRef,
          { selectedIndex, options, selectOptionAndCleanUp, setHighlightedIndex }
        ) =>
          anchorElementRef.current
            ? ReactDOM.createPortal(
              <div
                className='typeahead-popover auto-embed-menu'
                style={{
                  position: 'absolute',
                  zIndex: 1000,
                  marginLeft: `${Math.max(
                    parseFloat(anchorElementRef.current.style.width) - 200,
                    0
                  )}px`,
                  width: 200
                }}
              >
                <AutoEmbedMenu
                  options={options}
                  selectedItemIndex={selectedIndex}
                  onOptionClick={(option, index) => {
                    setHighlightedIndex(index)
                    selectOptionAndCleanUp(option)
                  }}
                  onOptionMouseEnter={(index) => {
                    setHighlightedIndex(index)
                  }}
                />
              </div>,
              anchorElementRef.current
            )
            : null
        }
      />
    </>
  )
}
